import { Hit, SearchResponse } from '@algolia/client-search';
import {
  AfterViewInit,
  Component, ElementRef, EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  Output, QueryList, ViewChild, ViewChildren,
  forwardRef
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { IonInput, IonItem, ModalController } from '@ionic/angular';
import { ME_Fit } from 'me-fit-typings';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AlgoliaSdkType, AlgoliaService } from 'src/app/services/algolia.service';
import { ExerciseFilterModalComponent } from '../../modals/exercise-filter-modal/exercise-filter-modal.component';
import { ExerciseRendererService } from '../exercise-renderer.service';


export type ExerciseSearchOptionValue = ME_Fit.Exercise;

@Component({
  selector: 'app-exercise-search',
  templateUrl: './exercise-search.component.html',
  styleUrls: ['./exercise-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ExerciseSearchComponent),
      multi: true,
    },
  ],
})
export class ExerciseSearchComponent implements OnDestroy, AfterViewInit, ControlValueAccessor {
  @HostListener('window:keyup.Escape', ['$event']) onEscapePress(event: KeyboardEvent) {
    const autoCompleteListPresent = !this.hideAutoCompleteItems$.value;
    if (event && autoCompleteListPresent) {
      event.preventDefault();
      event.stopPropagation();

      this.hideAutoCompleteItems();

      this.focusInput();
    }

  }

  ionInputKeyboardListener = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'Tab') {
      this.tabPress(event);
    }
  };

  @Output() exerciseSelected = new EventEmitter<ExerciseSearchOptionValue>();

  @ViewChild(IonInput, { static: false }) ionInput: IonInput;

  @ViewChild('input', { static: false }) input: ElementRef<HTMLInputElement>;;

  @ViewChildren(IonItem, { read: ElementRef }) allIonItems: QueryList<ElementRef<HTMLElement>>;

  /** Small will decrease the padding of the component to fit in the ag-grid cell */
  @Input()
  size: 'compact' | 'normal' = 'normal';

  /** Used when multiple exercises are selected */
  @Output() onExercisesSelection = new EventEmitter<ME_Fit.Exercise[]>();

  @Input() showExerciseFilterButton = false;

  @Input() algoliaSdkType: AlgoliaSdkType = 'global';

  modal: HTMLIonModalElement;

  @HostBinding('class.is-compact')
  get isCompact(): boolean {
    return this.size === 'compact';
  }

  /** All ion-items in the auto complete list */
  public get ionItems(): Array<ElementRef<HTMLElement>> {
    // Omitting the first item because its the input field ion-items
    //
    return this.allIonItems.filter((_item, index) => index > 0);
  }

  private internalCtrl = new FormControl();

  isOpen = false;

  searchValue: string | null;

  hideAutoCompleteItems$ = new BehaviorSubject(true);

  exerciseAutoCompleteFn = (exercise: ME_Fit.Exercise) => exercise?.name;

  exerciseSearchResponse$ = new BehaviorSubject<SearchResponse<ME_Fit.Exercise>|null>(null);

  exerciseSearchHits$ = this.exerciseSearchResponse$.pipe(
    tap((exerciseSearchResponse) => console.log('[ExerciseSearchComponent:exerciseSearchHits$] exercises', exerciseSearchResponse)),
    map((response) => response?.hits ?? [])
  );

  showBackDrop$ = combineLatest([
    this.exerciseSearchHits$.pipe(
      map(hits => hits?.length > 0)
    ),
    this.hideAutoCompleteItems$
  ]).pipe(
    map(([hasItems, hideAutoCompleteItems]) => hasItems && !hideAutoCompleteItems)
  );

  onTouchedCallback: Function = () => { };

  constructor(private $exerciseRendererService: ExerciseRendererService, private modalCtrl: ModalController, private $algolia: AlgoliaService) { }

  async ngOnDestroy() {
    const input = await this.ionInput.getInputElement();

    input.removeEventListener('keydown', this.ionInputKeyboardListener);

    this.modal?.dismiss();
  }

  async ngAfterViewInit() {
    const input = await this.ionInput.getInputElement();
    input.addEventListener('keydown', this.ionInputKeyboardListener);
  }

  @HostListener('focusout')
  onBlur() {
    this.onTouchedCallback();
  }

  async writeValue(exerciseId: string) {
    console.log(
      `[ExerciseSearchComponent:writeValue] called with exerciseId`,
      exerciseId
    );
    if (exerciseId) {
      // We will disable the event as we load the relevant option
      //
      this.internalCtrl.disable({ emitEvent: false });
      try {
        const algoliaValue = (await this.$exerciseRendererService.getExerciseById(exerciseId));

        console.log(
          '[ExerciseSearchComponent:writeValue] value from algolia',
          algoliaValue
        );

        this.internalCtrl.setValue(algoliaValue, { emitEvent: false });
        this.searchValue = algoliaValue?.name ?? null;
      } catch (error) {
        console.error(
          `[ExerciseSearchComponent:writeValue] error getting exercise from algolia by id=${exerciseId} error=`,
          error
        );
      } finally {
        this.internalCtrl.enable({ emitEvent: false });
      }
    } else {
      this.internalCtrl.reset();
      this.searchValue = null;
    }
  }

  selectItem(exerciseItem: Hit<ME_Fit.Exercise>) {
    console.log(
      `[ExerciseSearchComponent:selectItem] selecteed`,
      exerciseItem
    );

    this.internalCtrl.setValue(exerciseItem);
    this.searchValue = exerciseItem?.name ?? null;

    this.hideAutoCompleteItems();

    this.exerciseSelected.emit(exerciseItem);
  }

  registerOnChange(changeFn: any): void {
    this.internalCtrl.valueChanges.subscribe(
      (value: ME_Fit.Exercise) => {
        console.log(
          `[ExerciseSearchComponent:registerOnChange] value change`,
          value
        );

        if (value) {
          changeFn(value.id);
        }
      }
    );
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.internalCtrl.disable() : this.internalCtrl.enable();
  }

  async openExerciseFilterModal() {
    this.modal = await this.modalCtrl.create({
      component: ExerciseFilterModalComponent,
      componentProps: {
        anonymous: this.algoliaSdkType === 'anonymous',
      },
      cssClass: 'fullscreen-modal'
    });

    await this.modal.present();

    const dismissEvent = await this.modal.onDidDismiss<ME_Fit.Exercise[]>();

    if (dismissEvent.data) {
      this.onExercisesSelection.emit(dismissEvent.data);
    }
  }

  async searchValueChange(newSearchValue: string) {
    if (newSearchValue) {
      this.hideAutoCompleteItems$.next(false);
      let response: SearchResponse<ME_Fit.Exercise> | null = null;

      if (this.algoliaSdkType === 'global') {
        response = await this.$algolia.searchGlobalIndex<ME_Fit.Exercise>(newSearchValue);
      } else if ( this.algoliaSdkType === 'scoped') {
        response = await this.$algolia.searchScopedIndex<ME_Fit.Exercise>(newSearchValue);
      } else if (this.algoliaSdkType === 'anonymous') {
        response = await this.$algolia.searchAnonymousIndex<ME_Fit.Exercise>(newSearchValue);
      }

      this.exerciseSearchResponse$.next(response);


    }

    this.searchValue = newSearchValue;

    this.focusInput();

    console.log(
      `[ExerciseSearchComponent:searchValueChange] value change`,
      newSearchValue
    );
  }

  setFocus() {
    this.focusInput();
  }

  hideAutoCompleteItems() {
    this.hideAutoCompleteItems$.next(true);
  }

  tabPress(event: Event) {
    // If autocomplete list is hidden, dont do anything.
    //
    if (this.hideAutoCompleteItems$.value) {
      return;
    }

    event.preventDefault();

    if (this.ionItems[0]) {
      console.log(
        `[ExerciseSearchComponent:tabPress] focusing on ion-item`,
        this.ionItems[0].nativeElement.innerText
      );
      setTimeout(() => {
        this.ionItems[0].nativeElement.focus();
      });
    }
  }

  ionItemArrowDown(event: Event, ionItemIndex: number) {
    event.preventDefault();
    const newItemIndex = ionItemIndex + 1;
    if (this.ionItems[newItemIndex]) {
      this.ionItems[newItemIndex].nativeElement.focus();
    }
  }

  ionItemArrowUp(event: Event, ionItemIndex: number) {
    event.preventDefault();
    const newItemIndex = ionItemIndex - 1;
    if (this.ionItems[newItemIndex]) {
      this.ionItems[newItemIndex].nativeElement.focus();
    } else if (newItemIndex === -1) {
      this.focusInput();
    }
  }

  ionItemKeyDown(event: KeyboardEvent, ionItemIndex: number, exerciseItem: any) {
    const lastItemIndex = this.ionItems.length - 1;

    // If tab was pressed on last item, we want to hide the auto complete list
    //
    if (event.key === 'Tab' && lastItemIndex === ionItemIndex) {
      this.hideAutoCompleteItems();
    }

    const autoCompleteShown = !this.hideAutoCompleteItems$.value;

    // Prevent form submission if autocomplete list is shown
    //
    if (event.key === 'Enter' && autoCompleteShown) {
      event.preventDefault();
      this.selectItem(exerciseItem);
    }

    // If any of these buttons were pressed, we want to return early. Otherwise we want to focus on the keyboard.
    //
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Tab') {
      return;
    }

    this.focusInput();
  }

  private focusInput() {
    this.ionInput?.setFocus();

    this.input?.nativeElement.focus();
  }
}
